import { useEffect, useState, useMemo } from "react";
import { Plus, Download, Trash2, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { generateCertificatePDF } from "../lib/certificatePdf";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

const CERT_TYPES = [
  { value: "baja", label: "Baja médica (Incapacidad Temporal)" },
  { value: "alta", label: "Alta médica" },
  { value: "justificante", label: "Justificante de asistencia" },
  { value: "ordinario", label: "Certificado ordinario" },
  { value: "aptitud", label: "Certificado de aptitud" },
  { value: "informe", label: "Informe médico" },
];

const emptyForm = {
  patient_id: "",
  type: "justificante",
  title: "",
  body: "",
  diagnosis: "",
  start_date: "",
  end_date: "",
  estimated_days: "",
  visit_datetime: "",
  fit_for: "",
  fit_verdict: "apto",
  restrictions: "",
  send_email: true,
};

function typeBadgeColor(type) {
  return {
    baja: "bg-red-100 text-red-700",
    alta: "bg-emerald-100 text-emerald-700",
    justificante: "bg-blue-100 text-blue-700",
    ordinario: "bg-slate-100 text-slate-700",
    aptitud: "bg-purple-100 text-purple-700",
    informe: "bg-amber-100 text-amber-700",
  }[type] || "bg-slate-100 text-slate-700";
}

function typeLabel(type) {
  return CERT_TYPES.find((t) => t.value === type)?.label || type;
}

export default function Certificates() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const isDoctor = user?.role === "doctor" || user?.role === "admin";

  const load = () => api.get("/certificates").then(({ data }) => setItems(data));

  useEffect(() => {
    load();
    if (isDoctor) {
      // Prefer /patients (canonical). Fallback to /appointments for legacy setups.
      api.get("/patients")
        .then(({ data }) => setPatients(data.map((p) => ({ id: p.id, name: p.name }))))
        .catch(() => {
          api.get("/appointments").then(({ data }) => {
            const map = {};
            data.forEach((a) => { if (a.patient_id) map[a.patient_id] = a.patient_name; });
            setPatients(Object.entries(map).map(([id, name]) => ({ id, name })));
          }).catch(() => {});
        });
    }
  }, [isDoctor]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Cleanup empty optional fields
      if (!payload.estimated_days) delete payload.estimated_days;
      else payload.estimated_days = parseInt(payload.estimated_days);
      ["start_date", "end_date", "visit_datetime", "fit_for", "fit_verdict", "restrictions", "diagnosis", "title"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      const { data } = await api.post("/certificates", payload);
      toast.success(data.email_sent ? "Certificado creado y enviado por email" : "Certificado creado");
      setOpen(false);
      setForm(emptyForm);
      load();
      generateCertificatePDF(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este certificado? Esta acción es irreversible.")) return;
    try {
      await api.delete(`/certificates/${id}`);
      toast.success("Certificado eliminado");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const showBaja = form.type === "baja";
  const showAlta = form.type === "alta";
  const showJust = form.type === "justificante";
  const showApt = form.type === "aptitud";

  const filteredItems = useMemo(() => items, [items]);

  return (
    <div className="space-y-6 fade-up" data-testid="certificates-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl flex items-center gap-3">
          <FileSignature className="w-8 h-8 text-primary" />
          Certificados médicos
        </h1>
        {isDoctor && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="new-certificate-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo certificado
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Emitir certificado médico</DialogTitle>
                <DialogDescription>
                  Se generará un PDF firmado electrónicamente (HMAC-SHA256 + sello de tiempo) y, si lo indica, se notificará por email al paciente.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Tipo de certificado</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                    <SelectTrigger data-testid="cert-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CERT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Paciente</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm((p) => ({ ...p, patient_id: v }))}>
                    <SelectTrigger data-testid="cert-patient"><SelectValue placeholder="Seleccione un paciente" /></SelectTrigger>
                    <SelectContent>
                      {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Título (opcional)</Label>
                  <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Se genera automáticamente si se deja vacío" data-testid="cert-title" />
                </div>

                {(showBaja || showAlta) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{showBaja ? "Inicio de baja" : "Fecha de alta"}</Label>
                      <Input type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} data-testid="cert-start-date" />
                    </div>
                    {showBaja && (
                      <div>
                        <Label>Días estimados</Label>
                        <Input type="number" min="0" value={form.estimated_days} onChange={(e) => setForm((p) => ({ ...p, estimated_days: e.target.value }))} data-testid="cert-est-days" />
                      </div>
                    )}
                  </div>
                )}

                {showJust && (
                  <div>
                    <Label>Fecha y hora de la visita</Label>
                    <Input type="datetime-local" value={form.visit_datetime} onChange={(e) => setForm((p) => ({ ...p, visit_datetime: e.target.value }))} data-testid="cert-visit-datetime" />
                  </div>
                )}

                {showApt && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Actividad</Label>
                        <Input value={form.fit_for} onChange={(e) => setForm((p) => ({ ...p, fit_for: e.target.value }))} placeholder="p.ej. Fútbol federado, oposición, etc." data-testid="cert-fit-for" />
                      </div>
                      <div>
                        <Label>Veredicto</Label>
                        <Select value={form.fit_verdict} onValueChange={(v) => setForm((p) => ({ ...p, fit_verdict: v }))}>
                          <SelectTrigger data-testid="cert-fit-verdict"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="apto">Apto</SelectItem>
                            <SelectItem value="apto_con_restricciones">Apto con restricciones</SelectItem>
                            <SelectItem value="no_apto">No apto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}

                {(showBaja || showAlta || showApt) && (
                  <div>
                    <Label>{showApt ? "Restricciones" : "Recomendaciones / restricciones"}</Label>
                    <Input value={form.restrictions} onChange={(e) => setForm((p) => ({ ...p, restrictions: e.target.value }))} data-testid="cert-restrictions" />
                  </div>
                )}

                {form.type === "informe" && (
                  <div>
                    <Label>Diagnóstico *</Label>
                    <Input required value={form.diagnosis} onChange={(e) => setForm((p) => ({ ...p, diagnosis: e.target.value }))} data-testid="cert-diagnosis" />
                  </div>
                )}

                <div>
                  <Label>Cuerpo del certificado</Label>
                  <Textarea required rows={5} value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} placeholder="Texto que aparecerá en el PDF..." data-testid="cert-body" />
                </div>

                <div className="flex items-center gap-2">
                  <input id="cert-send-email" type="checkbox" checked={form.send_email} onChange={(e) => setForm((p) => ({ ...p, send_email: e.target.checked }))} data-testid="cert-send-email" />
                  <Label htmlFor="cert-send-email" className="cursor-pointer">Enviar por email al paciente</Label>
                </div>

                <Button type="submit" disabled={saving || !form.patient_id || !form.body} className="w-full bg-primary hover:bg-primary/90" data-testid="cert-save-btn">
                  {saving ? "Emitiendo..." : "Emitir y firmar"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display">Certificados emitidos</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {filteredItems.length === 0 && <div className="text-sm text-muted-foreground">Sin certificados aún.</div>}
          {filteredItems.map((c) => (
            <div key={c.id} className="border border-border rounded-lg p-4 flex flex-wrap justify-between gap-3" data-testid={`cert-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={typeBadgeColor(c.cert_type)}>{typeLabel(c.cert_type)}</Badge>
                  <span className="text-xs text-muted-foreground">{c.code} · {new Date(c.created_at).toLocaleString("es-ES")}</span>
                  {c.email_sent && <Badge variant="outline" className="text-xs">Enviado</Badge>}
                  {c.signature?.short && (
                    <span className="text-[10px] text-muted-foreground font-mono">Firma {c.signature.short}</span>
                  )}
                </div>
                <div className="font-medium mt-1 truncate">{c.title}</div>
                <div className="text-xs text-muted-foreground">
                  {user?.role === "patient" ? c.doctor_name : c.patient_name}
                </div>
              </div>
              <div className="flex gap-2 items-start">
                <Button variant="outline" size="sm" onClick={() => generateCertificatePDF(c)} data-testid={`cert-pdf-${c.id}`}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                {isDoctor && (
                  <Button variant="outline" size="icon" onClick={() => remove(c.id)} data-testid={`cert-delete-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
